import React, { useState, useCallback, useRef, useEffect, useContext, Suspense } from 'react';
import { debateEditorPlaceholder, getDefaultEditorPlaceholder, linkpostEditorPlaceholder, questionEditorPlaceholder } from '@/lib/editor/defaultEditorPlaceholder';
import { getLSHandlers, getLSKeyPrefix } from '../editor/localStorageHandlers';
import { userCanCreateCommitMessages } from '../../lib/betas';
import { useCurrentUser } from '../common/withUser';
import { Editor, EditorChangeEvent, getUserDefaultEditor, getInitialEditorContents, getBlankEditorContents, EditorContents, isBlank, EditorTypeString, styles, shouldSubmitContents, isValidEditorType, autosaveInterval, type LegacyEditorTypeString } from './Editor';
import { isCollaborative, PostCategory } from '../../lib/collections/posts/helpers';
import { DynamicTableOfContentsContext } from '../common/sharedContexts';
import { AppendToEditorContext } from './AppendToEditorContext';
import { useDebouncedCallback, useStabilizedCallback } from '../hooks/useDebouncedCallback';
import { useMessages } from '../common/withMessages';
import { CKEditorPortalProvider } from '../editor/CKEditorPortalProvider';
import { defineStyles, useStyles } from '../hooks/useStyles';
import { FieldValueSetter } from '@/components/tanstack-form-components/BaseAppForm';
import LastEditedInWarning from "./LastEditedInWarning";
import LocalStorageCheck from "./LocalStorageCheck";
import EditorTypeSelect from "./EditorTypeSelect";
import ErrorBoundary from "../common/ErrorBoundary";

const getPostPlaceholder = (post: PostsBase) => {
  const { question, postCategory } = post;
  const effectiveCategory = question ? "question" as const : postCategory as PostCategory;

  if (post.debate) return debateEditorPlaceholder; // note: this version of debates are deprecated in favor of post.collabEditorDialogue
  if (effectiveCategory === "question") return questionEditorPlaceholder;
  if (effectiveCategory === "linkpost") return linkpostEditorPlaceholder;
  return getDefaultEditorPlaceholder();
};

const definedStyles = defineStyles('EditorFormComponent', styles);

type EditorSubmitCallback = () => Promise<void>;
type EditorSuccessCallback<R> = (result: R, submitOptions?: { redirectToEditor?: boolean; noReload?: boolean }) => void;

export type AddOnSuccessCallback<R> = (fn: EditorSuccessCallback<R>) => () => void;
export type AddOnSubmitCallback<Fragment> = (cb: EditorSubmitCallback) => () => void;

interface EditorFormComponentProps<S, R> {
  field: FieldValueSetter<AnyBecauseHard> & { handleChange: (value: AnyBecauseHard) => void };
  commentEditor?: boolean;
  commentStyles?: boolean;
  commentMinimalistStyle?: boolean;
  collectionName: CollectionNameString;
  hideControls?: boolean;
  formType: 'new' | 'edit';
  editorHintText?: string;
  maxHeight?: boolean;
  document: any;
  name: string;
  fieldName: string;
  hintText: string;
  placeholder?: string;
  label?: string;
  revisionsHaveCommitMessages?: boolean;
  hasToc?: boolean;
  setFieldEditorType?: (editorType: EditorTypeString) => void;
  /**
   * Called on every editor change with whether the contents are blank. Unlike
   * the form field value (which is only updated on a throttle), this fires
   * immediately, so it's suitable for enabling/disabling a submit button.
   */
  onBlankStateChange?: (isBlank: boolean) => void;
  addOnSubmitCallback: (fn: EditorSubmitCallback) => () => void;
  addOnSuccessCallback: (fn: EditorSuccessCallback<R>) => () => void;
  getLocalStorageId?: (doc: any, name: string) => { id: string, verify: boolean }
}

export function useEditorFormCallbacks<R>() {
  const onSubmitCallback = useRef<EditorSubmitCallback | null>(null);
  const onSuccessCallback = useRef<EditorSuccessCallback<R> | null>(null);

  const addOnSubmitCallback = (cb: EditorSubmitCallback) => {
    onSubmitCallback.current = cb;
    return () => {
      onSubmitCallback.current = null;
    };
  };

  const addOnSuccessCallback = (cb: EditorSuccessCallback<R>) => {
    onSuccessCallback.current = cb;
    return () => {
      onSuccessCallback.current = null;
    };
  };

  return {
    onSubmitCallback,
    onSuccessCallback,
    addOnSubmitCallback,
    addOnSuccessCallback,
  };
};

function getDefaultLocalStorageIdGenerator<N extends CollectionNameString>(collectionName: N) {
  return function defaultLocalStorageIdGenerator(doc: any, name: string): {id: string, verify: boolean} {
    const { _id, conversationId } = doc
    if (_id && name) { return {id: `${_id}${name}`, verify: true}}
    else if (_id) { return {id: _id, verify: true }}
    else if (conversationId) { return {id: conversationId, verify: true }}
    else if (name) { return {id: `${collectionName}_new_${name}`, verify: true }}
    else {
      throw Error(`Can't get storage ID for this document: ${doc}`)
    }
  }
}

function InnerEditorFormComponent<S, R>({
  field,
  commentEditor,
  commentStyles,
  commentMinimalistStyle,
  collectionName,
  hideControls,
  formType,
  editorHintText,
  maxHeight,
  document,
  name,
  fieldName,
  hintText,
  placeholder,
  label,
  revisionsHaveCommitMessages,
  hasToc,
  setFieldEditorType,
  onBlankStateChange,
  addOnSubmitCallback,
  addOnSuccessCallback,
  getLocalStorageId,
}: EditorFormComponentProps<S, R>) {
  const classes = useStyles(definedStyles);
  const { flash } = useMessages();
  const currentUser = useCurrentUser();
  const editorRef = useRef<Editor|null>(null);
  const hasUnsavedDataRef = useRef({hasUnsavedData: false});
  const isCollabEditor = collectionName === 'Posts' && isCollaborative(document, fieldName);
  const appendToEditorContext = useContext(AppendToEditorContext);
  const registerAppendToEditor = appendToEditorContext?.registerAppendToEditor;
  

  const localStorageIdGenerator = getLocalStorageId ?? getDefaultLocalStorageIdGenerator(collectionName);

  const getLocalStorageHandlers = useCallback((editorType: EditorTypeString) => {
    return getLSHandlers(localStorageIdGenerator, document, name,
      getLSKeyPrefix(editorType)
    );
  }, [document, localStorageIdGenerator, name]);

  const getNewPostLocalStorageHandlers = useCallback((editorType: EditorTypeString) => {
    // It would be nice to check that it's a post and not a comment, but it should be fine
    // in comments as well, because there should always be content when editing a comment
    // (which we check later).
    if (formType !== "edit" || document.contents_latest || document.title !== 'Untitled Draft') {
      return {
        get: () => null,
        set: () => {},
        reset: () => {},
      };
    }
    // pass in {_id: null} to getLocalStorageId to treat this like a new post, without having to change how makeEditableOptions works
    const getLocalStorageId = (_: AnyBecauseIsInput, name: string) => localStorageIdGenerator({ _id: null }, name);
    return getLSHandlers(getLocalStorageId, document, name, getLSKeyPrefix(editorType));
  }, [document, name, localStorageIdGenerator, formType]);

  const [contents,setContents] = useState(() => getInitialEditorContents(
    field.state.value, document, fieldName, currentUser
  ));

  const [initialEditorType] = useState(contents.type);

  const dynamicTableOfContents = useContext(DynamicTableOfContentsContext)

  const defaultEditorType = getUserDefaultEditor(currentUser);
  const currentEditorType = contents.type || defaultEditorType;
  const isLexicalCollaborativeDocument = (
    collectionName === 'Posts'
    && fieldName === 'contents'
    && currentEditorType === 'lexical'
    && !!document?._id
  );
  const suppressLocalStorageRestore = isCollabEditor || isLexicalCollaborativeDocument;

  // We used to show this warning to a variety of editor types, but now we only want
  // to show it to people using the html editor. Converting from markdown to ckEditor
  // is error prone and we don't want to encourage it. We no longer support draftJS
  // but some old posts still are using it so we show the warning for them too.
  const showEditorWarning = (formType !== "new") && (currentEditorType === 'html' || (currentEditorType as LegacyEditorTypeString) === 'draftJS')

  const saveBackup = useCallback((newContents: EditorContents) => {
    // `document` contains live form values, not necessarily saved contents.
    // Only clear nonblank backups after a successful submission.
    if (isBlank(newContents)) {
      getLocalStorageHandlers(currentEditorType).reset();
      hasUnsavedDataRef.current.hasUnsavedData = false;
    } else {
      const success = getLocalStorageHandlers(newContents.type).set(newContents);

      if (success) {
        hasUnsavedDataRef.current.hasUnsavedData = false;
      }
    }
  }, [getLocalStorageHandlers, currentEditorType]);

  /**
   * Update the edited field (e.g. "contents") so that other form components can access the updated value. The direct motivation for this
   * was for SocialPreviewUpload, which needs to know the body of the post in order to generate a preview description and image.
   */
  const throttledSetContentsValue = useDebouncedCallback(async (newContents: EditorContents) => {
    if (!(editorRef.current && shouldSubmitContents(editorRef.current))) return

    // Preserve other fields in "contents" which may have been sent from the server
    const newFieldValue = {
      ...(document[fieldName] || {}),
      ...(await editorRef.current.submitData()),
    };

    // submitData reads from Editor props which may be stale during
    // leading-edge calls (React hasn't re-rendered yet). For editor types
    // that read content from props (everything except CKEditor, which reads
    // from its own internal state), override with the contents we received
    // directly as an argument.
    if (newContents.type !== 'ckEditorMarkup' && newFieldValue.originalContents) {
      newFieldValue.originalContents.data = newContents.value;
    }

    field.handleChange(newFieldValue);
  }, {
    rateLimitMs: autosaveInterval,
    callOnLeadingEdge: true,
    onUnmount: "cancelPending",
    allowExplicitCallAfterUnmount: false,
  });

  const throttledSaveBackup = useDebouncedCallback(saveBackup, {
    rateLimitMs: autosaveInterval,
    callOnLeadingEdge: false,
    onUnmount: "cancelPending",
    allowExplicitCallAfterUnmount: false,
  });

  const wrappedSetContents = useStabilizedCallback((change: EditorChangeEvent) => {
    const {contents: newContents, autosave} = change;
    if (dynamicTableOfContents && hasToc) {
      dynamicTableOfContents.setToc(change.contents);
    }
    setContents(newContents);
    
    // Only save to localStorage if not using collaborative editing, since with
    // collaborative editing stuff is getting constantly sent through a
    // websocket and saved that way.
    if (!isCollabEditor) {
      if (!isBlank(newContents)) {
        hasUnsavedDataRef.current.hasUnsavedData = true;
      }
    }
    
    // Update the parent form's state with the editor type on every keystroke, to enable other
    // form components (in particular PostSharingSettings) to check whether we're
    // using CkEditor vs draftjs vs etc. Update the actual contents with a throttled
    // callback to improve performance. Note that the contents are always recalculated on
    // submit anyway, setting them here is only for the benefit of other form components (e.g. SocialPreviewUpload)
    setFieldEditorType?.(newContents?.type)
    onBlankStateChange?.(isBlank(newContents))
    void throttledSetContentsValue(newContents)
    
    if (autosave) {
      throttledSaveBackup(newContents);
    }
  });

  useEffect(() => {
    if (!registerAppendToEditor) return;
    registerAppendToEditor((html: string) => {
      const editor = editorRef.current;
      const activeType = editor?.props?.value?.type;
      const ckEditorReference = editor?.state?.ckEditorReference;

      if (activeType === 'ckEditorMarkup' && ckEditorReference) {
        const currentData = ckEditorReference.getData() || '';
        const separator = currentData.trim() ? '<p><br></p>' : '';
        ckEditorReference.data.set(currentData + separator + html);
        return;
      }

      if (activeType === 'lexical') {
        const currentData = editor?.props?.value?.value ?? '';
        const separator = currentData.trim() ? '<p><br></p>' : '';
        wrappedSetContents({
          contents: { type: 'lexical', value: `${currentData}${separator}${html}` },
          autosave: true,
        });
      }
    });

    return () => {
      registerAppendToEditor(() => {});
    };
  }, [registerAppendToEditor, wrappedSetContents]);
  
  const hasGeneratedFirstToC = useRef({generated: false});
  useEffect(() => {
    if (dynamicTableOfContents && contents && !hasGeneratedFirstToC.current.generated && hasToc) {
      dynamicTableOfContents.setToc(contents);
      hasGeneratedFirstToC.current.generated = true;
    }
  }, [contents, dynamicTableOfContents, hasToc]);

  useEffect(() => {
    const unloadEventListener = (ev: BeforeUnloadEvent) => {
      if (hasUnsavedDataRef?.current?.hasUnsavedData) {
        ev.preventDefault();
        ev.returnValue = 'Are you sure you want to close?';
        return ev.returnValue
      }
    };
    
    window.addEventListener("beforeunload", unloadEventListener);
    return () => {
      window.removeEventListener("beforeunload", unloadEventListener);
    };
  }, [fieldName, hasUnsavedDataRef]);
  
  const onRestoreLocalStorage = useCallback((newState: EditorContents) => {
    if (suppressLocalStorageRestore) {
      // If in collab editing mode, we can't edit the editor contents.
      flash("Restoring from local storage is not supported in the collaborative editor. Use the Version History button to restore old versions.");
    } else {
      wrappedSetContents({contents: newState, autosave: false});
      // TODO: Focus editor
    }
  }, [wrappedSetContents, flash, suppressLocalStorageRestore]);

  const onRestoreNewPostLegacy = useCallback((newState: EditorContents) => {
    if (suppressLocalStorageRestore) {
      // If in collab editing mode, we can't edit the editor contents.
      flash("Restoring from local storage is not supported in the collaborative editor. Use the Version History button to restore old versions.");
    } else {
      wrappedSetContents({contents: newState, autosave: false});
      getNewPostLocalStorageHandlers(currentEditorType).reset();
      // TODO: Focus editor
    }
  }, [wrappedSetContents, flash, suppressLocalStorageRestore, currentEditorType, getNewPostLocalStorageHandlers]);
  
  
  useEffect(() => {
    if (editorRef.current) {
      const cleanupSubmitForm = addOnSubmitCallback(async () => {
        if (editorRef.current && shouldSubmitContents(editorRef.current)) {
          const updatedEditorData = await editorRef.current.submitData({ includeYjsState: true });
          if (!isCollabEditor) {
            saveBackup({
              type: updatedEditorData.originalContents.type,
              value: updatedEditorData.originalContents.data ?? '',
            });
          }
          field.setValue(updatedEditorData);
        }
      });
      const cleanupSuccessForm = addOnSuccessCallback((result: R, submitOptions?: { redirectToEditor?: boolean; noReload?: boolean }) => {
        getLocalStorageHandlers(currentEditorType).reset();
        // If we're autosaving (noReload: true), don't clear the editor!  Also no point in clearing it if we're getting redirected anyways
        if (editorRef.current && (!submitOptions?.redirectToEditor && !submitOptions?.noReload) && !isCollabEditor) {

          // We have to adjust for a timing issue here that caused text to
          // persist in the comment box if you submit the form too quickly.
          // There's the visible state that the user can see, and there's the
          // state tracked in the Editor component (which is tracked in this
          // component as `contents` and updated with wrappedSetContents — see
          // the getInitialEditorContents hook), and they're different. The
          // Editor component state is updated to the visible state after a
          // debounce period of not typing (i.e. the autosaveInterval, currently
          // 3 seconds). In this cleanupSuccessForm function, we want to clear
          // the text field, which you'd ordinarily do by calling
          // wrappedSetContents with a blank contents. But the timing issue is:
          // if they submit the form without ever having paused typing for 3
          // seconds, the Editor component state (`contents`) will still be
          // empty, so if we set it to empty here (by setting it to
          // getBlankEditorContents), it won't trigger the onChange handler,
          // which is where the debounced state change is triggered, so the
          // visible-to-user state will remain and then get auto-saved. So if
          // we've submitted the form and the contents seems to be empty, we set
          // it to a dummy value and *then* set it to empty, to make sure that
          // onChange handler gets triggered.

          if (contents.value.length === 0) {
            wrappedSetContents({
              contents: {type: initialEditorType, value: 'dummy value to trigger onChange handler'},
              autosave: false,
            });
          }
          wrappedSetContents({
            contents: getBlankEditorContents(initialEditorType),
            autosave: false,
          });
        }
        return result;
      });
      return () => {
        cleanupSubmitForm();
        cleanupSuccessForm();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!editorRef.current, fieldName, initialEditorType, addOnSuccessCallback, addOnSubmitCallback]);
  
  const fieldHasCommitMessages = revisionsHaveCommitMessages;
  const hasCommitMessages = fieldHasCommitMessages
    && currentUser && userCanCreateCommitMessages(currentUser)
    && (collectionName!=="Tags" || formType==="edit");

  const actualPlaceholder = (editorHintText || hintText || placeholder || (collectionName === "Posts" ? getPostPlaceholder(document) : undefined));

  if (!document) return null;

  return <div className={classes.root}>
    {showEditorWarning && 
      <LastEditedInWarning
        autoConvert={(contents.type as LegacyEditorTypeString) === 'draftJS'}
        initialType={initialEditorType}
        currentType={contents.type}
        defaultType={defaultEditorType}
        value={contents} setValue={wrappedSetContents}
      />
    }
    {!suppressLocalStorageRestore && <LocalStorageCheck
      getLocalStorageHandlers={getLocalStorageHandlers}
      onRestore={onRestoreLocalStorage}
      onRestoreNewPostLegacy={onRestoreNewPostLegacy}
      getNewPostLocalStorageHandlers={getNewPostLocalStorageHandlers}
    />}
    <CKEditorPortalProvider>
    {isValidEditorType(contents.type) && <Editor
      ref={editorRef}
      _classes={classes}
      currentUser={currentUser}
      label={label}
      formType={formType}
      documentId={document._id}
      collectionName={collectionName}
      fieldName={fieldName}
      formProps={{ maxHeight, commentMinimalistStyle }}
      isCollaborative={isCollabEditor}
      accessLevel={document.myEditorAccess}
      value={contents}
      onChange={wrappedSetContents}
      placeholder={actualPlaceholder}
      commentStyles={commentStyles}
      answerStyles={document.answer}
      questionStyles={document.question}
      commentEditor={commentEditor}
      hideControls={hideControls}
      maxHeight={maxHeight}
      hasCommitMessages={hasCommitMessages ?? undefined}
      document={document}
    />}
    </CKEditorPortalProvider>
    {!hideControls && (
      <EditorTypeSelect value={contents} setValue={wrappedSetContents} isCollaborative={isCollabEditor}/>
    )}
  </div>
}

export function EditorFormComponent<S, R>(props: EditorFormComponentProps<S, R>) {
  const { field, formType, ...rest } = props;
  if (typeof field.state.value !== 'object' && formType === 'edit') {
    return null;
  }

  return <ErrorBoundary>
    <Suspense>
      <InnerEditorFormComponent<S, R> {...{ field, formType, ...rest }} />
    </Suspense>
  </ErrorBoundary>;
}
